import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, ArrowRight, Search, Loader2, Sparkles, CheckCircle2, Phone } from "lucide-react";
import { Input } from "./ui/input";
import { trackPostcodeSearch, trackPhoneClick } from "../lib/tracking";

const RANGES = [
  // [min, max, state, cityLanding]
  [1000, 2599, "NSW", "sydney"],
  [2619, 2899, "NSW", "sydney"],
  [2921, 2999, "NSW", "sydney"],
  [2600, 2618, "ACT", "canberra"],
  [2900, 2920, "ACT", "canberra"],
  [200, 299, "ACT", "canberra"],
  [3000, 3999, "VIC", "melbourne"],
  [8000, 8999, "VIC", "melbourne"],
  [4000, 4999, "QLD", "brisbane"],
  [9000, 9999, "QLD", "brisbane"],
  [5000, 5999, "SA", "adelaide"],
  [6000, 6999, "WA", "perth"],
  [7000, 7999, "TAS", "hobart"],
  [800, 899, "NT", "darwin"],
  [900, 999, "NT", "darwin"],
];

const STATE_NAME = {
  NSW: "New South Wales", VIC: "Victoria", QLD: "Queensland", WA: "Western Australia",
  SA: "South Australia", ACT: "Australian Capital Territory", TAS: "Tasmania", NT: "Northern Territory",
};

const HUB_STATES = new Set(["VIC", "WA"]);

export default function PostcodeLookup() {
  const [pc, setPc] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const check = (e) => {
    e?.preventDefault();
    setError("");
    setResult(null);
    const pad = pc.padStart(4, "0");
    if (!/^\d{4}$/.test(pad)) {
      setError("Enter a 4-digit Australian postcode");
      return;
    }
    const n = parseInt(pad, 10);
    setLoading(true);
    // Simulate tiny loading window so users see feedback on instant local lookup.
    setTimeout(() => {
      const match = RANGES.find(([lo, hi]) => n >= lo && n <= hi);
      if (!match) {
        setError("That doesn't look like a valid AU postcode");
        setLoading(false);
        return;
      }
      const [, , state, cityLanding] = match;
      const hub = HUB_STATES.has(state);
      const payload = {
        postcode: pad,
        state,
        cityLanding,
        stateName: STATE_NAME[state],
        hub,
      };
      setResult(payload);
      setLoading(false);
      trackPostcodeSearch({ postcode: pad, state, hub });
    }, 420);
  };

  const goVacancies = () => navigate(`/vacancies?state=${result.state}`);
  const goLanding = () => navigate(`/sil-${result.cityLanding}`);
  const goContact = () => navigate("/contact");
  const goReferral = () => navigate("/referrals");

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[color:var(--alpha-line-strong)] p-5 md:p-6 bg-white"
      data-testid="postcode-lookup"
    >
      {/* Soft gradient wash */}
      <div
        className="absolute inset-0 -z-0 pointer-events-none opacity-90"
        style={{
          background:
            "radial-gradient(60% 100% at 0% 0%, rgba(232,223,245,0.85) 0%, transparent 65%), radial-gradient(50% 80% at 100% 100%, rgba(230,244,236,0.85) 0%, transparent 65%)",
        }}
      />
      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg alpha-gradient text-white flex items-center justify-center">
            <MapPin className="w-4 h-4" />
          </div>
          <div className="alpha-overline">Check your area</div>
        </div>
        <form onSubmit={check} className="mt-3 flex gap-2">
          <div className="relative flex-1">
            <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--alpha-mute)]" />
            <Input
              value={pc}
              onChange={(e) => setPc(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="Enter your postcode (e.g. 3000)"
              className="pl-9 h-11 bg-white"
              inputMode="numeric"
              maxLength={4}
              data-testid="postcode-input"
              aria-label="Postcode"
            />
          </div>
          <button
            type="submit"
            disabled={loading || pc.length < 3}
            className="alpha-btn-primary !px-5 !py-[10px] text-sm disabled:opacity-60"
            data-testid="postcode-check"
          >
            {loading ? <Loader2 className="w-4 h-4 alpha-spin" /> : <Search className="w-4 h-4" />}
            {loading ? "Checking" : "Check"}
          </button>
        </form>
        {error && (
          <div className="mt-3 text-sm text-red-600" data-testid="postcode-error">{error}</div>
        )}
        {result && (
          <div
            className={`mt-4 p-4 rounded-xl border ${
              result.hub
                ? "bg-[color:var(--alpha-green-100)] border-[#CCE5D6]"
                : "bg-[color:var(--alpha-purple-50)] border-[color:var(--alpha-purple-200)]"
            } alpha-fade-up`}
            data-testid="postcode-result"
          >
            <div className="flex items-start gap-2 text-sm text-[color:var(--alpha-ink)]">
              {result.hub ? (
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-[color:var(--alpha-green-700)] shrink-0" />
              ) : (
                <Sparkles className="w-4 h-4 mt-0.5 text-[color:var(--alpha-purple-700)] shrink-0" />
              )}
              <div>
                {result.hub ? (
                  <>
                    <span className="font-semibold">We actively service {result.stateName}.</span>{" "}
                    Postcode <strong>{result.postcode}</strong> sits in one of our operational hubs — we have vacancies and on-the-ground teams nearby and can start supports quickly.
                  </>
                ) : (
                  <>
                    <span className="font-semibold">We support your area nationally.</span>{" "}
                    Postcode <strong>{result.postcode}</strong> is in <strong>{result.stateName}</strong>. Alpha services participants across Australia through our national coordination model — chat to intake for capacity and onboarding.
                  </>
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {result.hub ? (
                <>
                  <button onClick={goVacancies} className="alpha-btn-primary !py-2 !px-4 text-sm" data-testid="postcode-go-vacancies">
                    View {result.state} vacancies <ArrowRight className="w-4 h-4" />
                  </button>
                  <button onClick={goReferral} className="alpha-btn-green !py-2 !px-4 text-sm" data-testid="postcode-go-referral">
                    Start a referral
                  </button>
                </>
              ) : (
                <>
                  <button onClick={goContact} className="alpha-btn-primary !py-2 !px-4 text-sm" data-testid="postcode-contact">
                    Contact intake <ArrowRight className="w-4 h-4" />
                  </button>
                  <button onClick={goReferral} className="alpha-btn-green !py-2 !px-4 text-sm" data-testid="postcode-go-referral">
                    Start a referral
                  </button>
                </>
              )}
              <button
                onClick={goLanding}
                className="alpha-btn-ghost !py-2 !px-4 text-sm"
                data-testid="postcode-go-landing"
              >
                Support in {result.stateName}
              </button>
              <a
                href="tel:1300380692"
                onClick={() => trackPhoneClick("1300 380 692", "postcode-result")}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--alpha-purple-700)] hover:text-[color:var(--alpha-purple-800)] px-2 py-2"
                data-testid="postcode-phone"
              >
                <Phone className="w-4 h-4" /> 1300 380 692
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
